import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import CroppedImage from '../CroppedImage';
import { toast } from 'sonner';

function AdminOrdersSection({ orders, testMode, onOrderDeleted, siteTimezone, formatDate, formatTime, formatDateTime }) {
  const [adminOrders, setAdminOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Log only once on mount, not every render
  useEffect(() => {
    console.log('AdminOrdersSection - siteTimezone:', siteTimezone);
  }, [siteTimezone]);
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showImages, setShowImages] = useState(false);
  const [error, setError] = useState(null);
  const [processingOrder, setProcessingOrder] = useState(null);
  const [outputFormat, setOutputFormat] = useState('zip');

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [ordersResponse, statsResponse] = await Promise.all([
        axios.get('/admin/orders'),
        axios.get('/admin/stats')
      ]);
      
      setAdminOrders(ordersResponse.data.orders || []);
      setStats(statsResponse.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        setError('Admin access required. You do not have permission to access this page.');
      } else {
        setError('Failed to load admin dashboard');
        toast.error('Failed to load admin dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  // Memoize formatted orders to avoid recalculating dates on every render
  const formattedOrders = useMemo(() => {
    return adminOrders.map(order => ({
      ...order,
      formattedDate: formatDate ? formatDate(order.created_at, siteTimezone) : new Date(order.created_at).toLocaleDateString(),
      formattedTime: formatTime ? formatTime(order.created_at, siteTimezone) : new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }));
  }, [adminOrders, formatDate, formatTime, siteTimezone]);

  const handleDeleteOrder = useCallback(async (orderId) => {
    if (!testMode) {
      toast.error('Enable Test Mode to delete orders');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/admin/orders/${orderId}`);
      toast.success('✅ Order deleted successfully');
      await fetchAdminData();
      if (onOrderDeleted) onOrderDeleted();
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error('Failed to delete order');
    }
  }, [testMode, onOrderDeleted]);

  const updateOrderStatus = useCallback(async (orderId, newStatus) => {
    try {
      await axios.post(`/admin/orders/${orderId}/update-status`, {
        status: newStatus
      });
      
      setAdminOrders(prev => 
        prev.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus }
            : order
        )
      );
      
      toast.success(`Order status updated to ${newStatus}`);
      fetchAdminData();
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status');
    }
  }, []);

  const downloadOrderImages = useCallback(async (orderId) => {
    try {
      const response = await axios.get(`/admin/orders/${orderId}/images`);
      setSelectedOrder(response.data);
      setShowImages(true);
    } catch (error) {
      console.error('Failed to fetch order images:', error);
      toast.error('Failed to load order images');
    }
  }, []);

  const downloadPrintFiles = useCallback(async (orderId, format = 'zip') => {
    try {
      setProcessingOrder(orderId);
      toast.info('🎨 Processing images from B2... This may take a few moments.');
      
      const response = await axios.post(`/admin/process-order-images/${orderId}`, {
        output_format: format
      }, {
        responseType: format === 'zip' ? 'blob' : 'json'
      });
      
      if (format === 'zip') {
        // Create a download link for ZIP
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `order-${orderId}-print-files.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('✅ Print files downloaded successfully!');
      } else if (format === 'b2') {
        toast.success(`✅ ${response.data.count} files uploaded to B2 successfully!`);
      } else {
        toast.success(`✅ Images processed successfully! ${response.data.file_count} files ready.`);
      }
    } catch (error) {
      console.error('Failed to process print files:', error);
      toast.error('❌ Failed to process print files: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingOrder(null);
    }
  }, []);

  const downloadImage = useCallback((imageData, filename) => {
    const link = document.createElement('a');
    link.href = `data:image/jpeg;base64,${imageData}`;
    link.download = filename;
    link.click();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🚫</div>
        <h3 className="text-xl font-semibold text-red-600 mb-2">Access Denied</h3>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  if (showImages && selectedOrder) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-gray-900">Order Images</h2>
          <Button 
            variant="outline"
            onClick={() => setShowImages(false)}
            data-testid="back-to-orders"
          >
            ← Back to Orders
          </Button>
        </div>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            Order #{selectedOrder.order_info.id.slice(-8)}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedOrder.images.map((image, index) => (
              <Card key={index} className="p-4">
                <img 
                  src={`data:image/jpeg;base64,${image.image_data}`}
                  alt={`Print order image: ${image.filename}, ${image.size}, ${image.paper_type} paper`}
                  className="w-full h-48 object-cover rounded mb-4"
                  style={{
                    transform: `rotate(${image.rotation || 0}deg)`
                  }}
                />
                <div className="space-y-2 text-sm">
                  <p><strong>File:</strong> {image.filename}</p>
                  <p><strong>Size:</strong> {image.size}</p>
                  <p><strong>Paper:</strong> {image.paper_type}</p>
                  <p><strong>Border:</strong> {image.border ? 'Yes' : 'No'}</p>
                  <p><strong>Quantity:</strong> {image.quantity}</p>
                  {image.scale && <p><strong>Scale:</strong> {image.scale}%</p>}
                </div>
                <Button 
                  size="sm"
                  className="w-full mt-3"
                  onClick={() => downloadImage(image.image_data, image.filename)}
                  data-testid={`download-${image.filename}`}
                >
                  Download High-Res
                </Button>
              </Card>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Orders Dashboard</h2>
        <p className="text-gray-600">Manage customer orders and download print files</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6 text-center">
            <h3 className="text-2xl font-bold text-blue-600">{stats.total_orders}</h3>
            <p className="text-gray-600">Total Orders</p>
          </Card>
          <Card className="p-6 text-center">
            <h3 className="text-2xl font-bold text-green-600">{stats.paid_orders}</h3>
            <p className="text-gray-600">Paid Orders</p>
          </Card>
          <Card className="p-6 text-center">
            <h3 className="text-2xl font-bold text-purple-600">{stats.items_to_print}</h3>
            <p className="text-gray-600">Items to Print</p>
          </Card>
          <Card className="p-6 text-center">
            <h3 className="text-2xl font-bold text-orange-600">
              ${(stats.total_revenue / 100).toFixed(2)}
            </h3>
            <p className="text-gray-600">Total Revenue</p>
          </Card>
        </div>
      )}

      {/* Orders Table */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Recent Orders</h3>
        
        {adminOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No orders found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2">Order ID</th>
                  <th className="text-left py-3 px-2">Customer</th>
                  <th className="text-left py-3 px-2">Items</th>
                  <th className="text-left py-3 px-2">Total</th>
                  <th className="text-left py-3 px-2">Status</th>
                  <th className="text-left py-3 px-2">Date</th>
                  <th className="text-left py-3 px-2">Time</th>
                  <th className="text-left py-3 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {formattedOrders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-2 font-medium">
                      #{order.id.slice(-8)}
                    </td>
                    <td className="py-3 px-2">
                      <div>
                        <p className="font-medium">{order.customer?.name || 'Unknown'}</p>
                        <p className="text-gray-500 text-xs">{order.customer?.email || ''}</p>
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      {order.items?.length || 0} items
                    </td>
                    <td className="py-3 px-2 font-medium">
                      ${(order.total_amount / 100).toFixed(2)}
                    </td>
                    <td className="py-3 px-2">
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className={`px-2 py-1 rounded text-xs border-0 ${
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'paid' ? 'bg-green-100 text-green-800' :
                          order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                          order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                        data-testid={`status-select-${order.id}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="py-3 px-2 text-gray-600">
                      {order.formattedDate}
                    </td>
                    <td className="py-3 px-2 text-gray-600">
                      {order.formattedTime}
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPrintFiles(order.id, 'zip')}
                          disabled={!order.items || order.items.length === 0 || processingOrder === order.id}
                          data-testid={`download-print-files-${order.id}`}
                          className="bg-orange-50 hover:bg-orange-100 text-custom-orange border-orange-300"
                        >
                          {processingOrder === order.id ? (
                            <>
                              <svg className="animate-spin h-4 w-4 mr-2 inline" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            '📥 Download Print Files'
                          )}
                        </Button>
                        {testMode && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteOrder(order.id)}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid={`delete-order-${order.id}`}
                          >
                            🗑️ Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// Orders Section Component


export default AdminOrdersSection;
